import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import PurchaseLotTableRow from './PurchaseLotTableRow'

import HoverTooltip from '../../../../../../../../components/HoverTooltip'
import LoadingIcon from '../../../../../../../../components/LoadingIcon'
import { CurrencyList, PurchaseLot, PurchaseStage } from '../../../../../types'

type Props = {
    purchaseGuid: string
    purchaseRegNumber: string
    purchaseAction: boolean
    theme: string
    isActive: boolean
    currencyList: CurrencyList[]
}

/** форматирует числа “1 234,56” и обрабатывает пустые значения */
const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-'
    const parsedNum = typeof num === 'number' ? num : parseFloat(num)
    if (isNaN(parsedNum)) return '-'
    return parsedNum
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        .replace('.', ',')
}

const PurchaseLotTable: React.FC<Props> = (props) => {
    const navigate = useNavigate()

    // ====== state ======

    const [purchaseLots, setPurchaseLots] = useState<PurchaseLot[]>([])
    const [purchaseLotsLoadedGuid, setPurchaseLotsLoadedGuid] = useState<
        string | null
    >(null)
    const [purchaseLotsLoading, setPurchaseLotsLoading] = useState(false)
    const [purchaseLotsError, setPurchaseLotsError] = useState<string | null>(
        null
    )

    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [
        deletePerformancePositionRowsPopup,
        setDeletePerformancePositionRowsPopup,
    ] = useState<boolean>(false)
    const [isDeleting, setDeleting] = useState<boolean>(false)
    const [deleteRows, setDeleteRows] = useState<PurchaseLot[]>([])

    // для отмены текущих фетчей
    const listAbortRef = useRef<AbortController | null>(null)
    const totalsAbortRef = useRef<AbortController | null>(null)

    const fetchPurchaseLotsRef = useRef<
        ((force?: boolean) => Promise<void>) | null
    >(null)

    // ====== loaders ======
    const fetchPurchaseLots = useCallback(
        async (force = false) => {
            if (!props.purchaseGuid) return
            if (!force && purchaseLotsLoadedGuid === props.purchaseGuid) return

            // важно: если форсим, сбросим метку
            if (force) setPurchaseLotsLoadedGuid(null)

            if (listAbortRef.current) listAbortRef.current.abort()
            const controller = new AbortController()
            listAbortRef.current = controller

            try {
                setPurchaseLotsLoading(true)
                setPurchaseLotsError(null)

                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/backend/v1/purchases/lots?purchase_guid=${props.purchaseGuid}`,
                    {
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        signal: controller.signal,
                    }
                )
                if (!res.ok) throw new Error('Failed to fetch purchase lots')
                const data: PurchaseLot[] = await res.json()
                setPurchaseLots(data ?? [])
                setPurchaseLotsLoadedGuid(props.purchaseGuid)

                // если стоял Select All — пересинхронизируем выбранные
                if (selectAll) {
                    setSelectedItems((data ?? []).map((i) => i.guid))
                } else {
                    // почистим выбранные, которых уже нет
                    setSelectedItems((prev) =>
                        prev.filter((id) =>
                            (data ?? []).some((d) => d.guid === id)
                        )
                    )
                }
            } catch (e: any) {
                if (e?.name !== 'AbortError') {
                    setPurchaseLotsError(
                        e?.message ?? 'Ошибка загрузки исполнения по договору'
                    )
                }
            } finally {
                setPurchaseLotsLoading(false)
            }
        },
        [props.purchaseGuid, purchaseLotsLoadedGuid, selectAll]
    )

    useEffect(() => {
        fetchPurchaseLotsRef.current = fetchPurchaseLots
    }, [fetchPurchaseLots])

    useEffect(() => {
        fetchPurchaseLots()
        return () => {
            listAbortRef.current?.abort()
        }
    }, [fetchPurchaseLots])

    return (
        <>
            <fieldset disabled={false}>
                <div
                    className={`flex flex-col w-full min-w-fit bg-[#e4e6e8] dark:bg-[#242225] rounded`}
                >
                    {/* toolbar */}
                    <div className="flex flex-row gap-1 p-1 h-11 rounded-t text-[11px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                        <div className="flex w-8 h-full p-1 items-center justify-center text-center">
                            <div className="w-6 h-6 grid rounded-full place-items-center cursor-pointer hover:bg-[#FFFFFF] dark:hover:bg-[#656365]">
                                <HoverTooltip tooltipText="Выбрать все">
                                    <input
                                        type="checkbox"
                                        className="p-2 cursor-pointer"
                                        checked={selectAll}
                                        readOnly
                                    />
                                </HoverTooltip>
                            </div>
                        </div>
                        <div className="flex w-8 gap-1 items-center justify-center text-center">
                            {/* Добавить */}
                            <button disabled={true}>
                                <HoverTooltip tooltipText="Добавить лот">
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="plus"
                                    >
                                        <path
                                            fill={
                                                !props.purchaseAction
                                                    ? props.theme == 'dark'
                                                        ? '#FFFFFF'
                                                        : '#181818'
                                                    : '#8A8A8A'
                                            }
                                            d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                        ></path>
                                    </svg>
                                </HoverTooltip>
                            </button>
                        </div>
                        <div className="flex w-8 gap-1 items-center justify-center text-center">
                            {/* Удалить */}
                            <button
                                disabled={true}
                                className={`flex w-6 h-6 rounded-full items-center justify-center`}
                            >
                                <HoverTooltip tooltipText="Удалить">
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="trash-alt"
                                    >
                                        <path
                                            fill={
                                                !props.purchaseAction
                                                    ? props.theme == 'dark'
                                                        ? '#FFFFFF'
                                                        : '#181818'
                                                    : '#8A8A8A'
                                            }
                                            d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                        ></path>
                                    </svg>
                                </HoverTooltip>
                            </button>
                        </div>
                        <div className="flex w-24 items-center justify-center text-center"></div>
                        <div className="flex w-24 items-center justify-center text-center"></div>
                    </div>

                    {/* name */}
                    <div className="flex flex-row gap-1 h-10 p-1 items-center justify-center text-[10px] text-[#616173] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                        <div className="flex w-8 h-full p-1 items-center justify-center text-center"></div>
                        <div className="flex w-8 items-center justify-center text-center">
                            №
                        </div>
                        <div className="flex flex-1 items-center justify-center text-center">
                            Наименование
                        </div>
                        <div className="flex w-28 items-center justify-center text-center">
                            Сумма лота
                        </div>
                    </div>

                    {/* data */}
                    <div className="flex flex-col w-full gap-1 my-1 overflow-y custom-scrollbar">
                        {purchaseLotsLoading ? (
                            <div className="flex w-full h-full items-center justify-center">
                                <LoadingIcon />
                            </div>
                        ) : (
                            purchaseLots?.map((lot, index) => (
                                <PurchaseLotTableRow
                                    key={lot.guid}
                                    purchaseGuid={props.purchaseGuid}
                                    purchaseAction={props.purchaseAction}
                                    purch_number={index + 1}
                                    theme={props.theme}
                                    purchaseLot={lot}
                                    isActive={props.isActive}
                                    currencyList={props.currencyList}
                                    selected={selectedItems.includes(lot.guid)}
                                    // onClick={(perf_guid) => {
                                    //     handleCheckboxChange(perf_guid)
                                    // }}
                                    onDoubleClick={(purch_lot_guid) => {
                                        const perf_action = props.purchaseAction
                                            ? 'edit'
                                            : 'draft'

                                        navigate(
                                            `/hub/purchase/${props.purchaseRegNumber}/purchases/${perf_action}/${props.purchaseGuid}/lot/${purch_lot_guid}`,
                                            {
                                                state: {
                                                    from: 'purchase_lot',
                                                    purchaseRegNumber:
                                                        props.purchaseRegNumber,
                                                },
                                            }
                                        )
                                    }}
                                />
                            ))
                        )}
                    </div>

                    {/* footer */}
                    <div className="flex flex-row gap-1 h-11 text-[11px] p-1 rounded-b text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#333033]">
                        <div className="flex w-3 h-full p-1 items-center justify-center text-center"></div>
                        <div className="flex flex-1 gap-3 items-center justify-start">
                            <button
                                className={`flex flex-row gap-2 px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs 
                                    ${props.purchaseAction ? 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#ededed] dark:bg-[#494849]' : 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'}
                                `}
                                disabled={!props.purchaseAction}
                            >
                                <svg
                                    className="w-4 h-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="plus"
                                >
                                    <path
                                        fill={
                                            !props.purchaseAction
                                                ? props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                                : '#8A8A8A'
                                        }
                                        d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                    ></path>
                                </svg>
                                Добавить лот
                            </button>
                            <button
                                className={`flex flex-row gap-2 px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs 
                                        ${props.purchaseAction ? 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#ededed] dark:bg-[#494849]' : 'shadow-md shadow-[#18181830] text-[#181818] bg-[#F6F6F6] hover:bg-[#ed9cae] dark:bg-[#494849] dark:hover:bg-[#701f1f] dark:text-[#FFFFFF]'}
                                    `}
                                disabled={!props.purchaseAction}
                            >
                                <svg
                                    className="w-4 h-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="plus"
                                >
                                    <path
                                        fill={
                                            !props.purchaseAction
                                                ? props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                                : '#8A8A8A'
                                        }
                                        d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                    ></path>
                                </svg>
                                Удалить
                            </button>
                        </div>
                        <div className="flex w-20 items-center justify-center text-center"></div>
                        <div className="flex w-20 items-center justify-center text-center"></div>
                    </div>
                </div>
            </fieldset>
        </>
    )
}

export default PurchaseLotTable
